import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import GokuDataGrid from '@/components/GokuDataGrid'
import {
    getResellerCustomers,
    removeResellerCustomer,
} from '@/modules/Reseller/services'
import { columns } from './columns'
import AddIcon from '@/icons/AddIcons'
import ButtonGroupActions from '@/components/ButtonGroupActions'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import AddCustomerDialog from './AddCustomerDialog'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button as MuiButton,
} from '@mui/material'

interface CustomersProps {
    resellerId: number
}

const Customers: React.FC<CustomersProps> = ({ resellerId }) => {
    const { t } = useTranslation('reseller')
    const queryClient = useQueryClient()
    const [addDialogOpen, setAddDialogOpen] = useState(false)
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
    const [customerToRemove, setCustomerToRemove] = useState<number | null>(
        null
    )

    const queryKey = [QUERY_KEYS.RESELLER_CUSTOMERS, resellerId.toString()]

    // Remove mutation
    const { mutateAsync: removeMutation, isPending: isRemoving } =
        useMutationWithHandler(
            { mutationFn: removeResellerCustomer },
            {
                reactQueryKeys: QUERY_KEYS.RESELLER_CUSTOMERS,
                successMessage: t('customer_removed_successfully'),
            }
        )

    const handleRemoveClick = (customerId: number) => {
        setCustomerToRemove(customerId)
        setRemoveDialogOpen(true)
    }

    const handleRemoveConfirm = async () => {
        if (customerToRemove) {
            await removeMutation({
                reseller_id: resellerId,
                customer_id: customerToRemove,
            })
            setRemoveDialogOpen(false)
            setCustomerToRemove(null)
        }
    }

    const handleAddSuccess = () => {
        setAddDialogOpen(false)
        queryClient.invalidateQueries({ queryKey })
    }

    return (
        <>
            <ButtonGroupActions
                actions={[
                    {
                        icon: <AddIcon />,
                        label: t('add_customer'),
                        onClick: () => setAddDialogOpen(true),
                    },
                ]}
            />
            <GokuDataGrid
                apiCall={(payload) => getResellerCustomers(resellerId, payload)}
                columns={columns(t, handleRemoveClick)}
                reactQueryKey={QUERY_KEYS.RESELLER_CUSTOMERS}
                dataGridProps={{
                    getRowId: (row) => row?.id,
                }}
                options={{
                    pageSize: 10,
                    showSearch: false,
                    showHideColumn: false,
                }}
            />

            {/* Add Customer Dialog */}
            <AddCustomerDialog
                open={addDialogOpen}
                resellerId={resellerId}
                onClose={() => setAddDialogOpen(false)}
                onSuccess={handleAddSuccess}
            />

            {/* Remove Confirmation Dialog */}
            <Dialog
                open={removeDialogOpen}
                onClose={() => setRemoveDialogOpen(false)}
            >
                <DialogTitle>{t('remove_customer')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('remove_customer_confirmation')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={() => setRemoveDialogOpen(false)}>
                        {t('cancel')}
                    </MuiButton>
                    <MuiButton
                        onClick={handleRemoveConfirm}
                        color="error"
                        disabled={isRemoving}
                    >
                        {isRemoving ? t('removing') : t('remove')}
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default Customers
